/** @format */
import { Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { dateUtils } from 'common-typescript/constants';
import { OtmId, StudyRightCancellation, StudyRightPassivation, StudyRightState, StudyRightStatePeriod } from 'common-typescript/types';
import * as _ from 'lodash-es';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { ComponentDowngradeMappings, DowngradedComponent, ModalService, StaticMembers } from 'sis-common';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { StudyRightTermRegistrationsEntityService } from '../../service/study-right-term-registrations-entity.service';
import { StudyRightDecisionModalComponent } from './study-right-decision-modal/study-right-decision-modal.component';

@StaticMembers<DowngradedComponent>()
@Component({
    selector: 'sis-study-right-states',
    templateUrl: './study-right-states.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StudyRightStatesComponent implements OnDestroy {
    static downgrade: ComponentDowngradeMappings = {
        moduleName: 'sis-components.studyRight.studyRightStatesNg',
        directiveName: 'sisStudyRightStates',
    };

    private static readonly CANCELLED_STATES: StudyRightState[] = ['CANCELLED_BY_ADMINISTRATION', 'RESCINDED', 'TERMINATED'];

    constructor(
        private studyRightTermRegistrationsEntityService: StudyRightTermRegistrationsEntityService,
        private modalService: ModalService,
        private appErrorHandler: AppErrorHandler,
    ) {}

    @Input() studyRightCancellation: StudyRightCancellation;
    @Input() studyRightPassivations: StudyRightPassivation[];

    // Explicit state periods, e.g. computed for a specific (non-current) study right snapshot. When given, these are shown instead of
    // the ones fetched by studyRightId, which always reflect the study right's current, live state.
    @Input() set studyRightStatePeriods(statePeriods: StudyRightStatePeriod[]) {
        this.explicitStatePeriods = statePeriods;
        this.refreshStatePeriods();
    }

    @Input() set studyRightId(studyRightId: OtmId) {
        this.currentStudyRightId = studyRightId;
        this.refreshStatePeriods();
    }

    // Use this to manually trigger update on study right states view.
    @Input() set studyRightStatesUpdateTrigger(studyRightIdSubject: Subject<OtmId>) {
        this.studyRightIdSubscription = studyRightIdSubject.subscribe((studyRightId: OtmId) => {
            this.currentStudyRightId = studyRightId;
            this.refreshStatePeriods();
        });
    }

    statePeriods: StudyRightStatePeriod[] = [];
    activeStatePeriod: StudyRightStatePeriod;
    studyRightIdSubscription: Subscription;

    private explicitStatePeriods: StudyRightStatePeriod[];
    private currentStudyRightId: OtmId;
    private subscription = Subscription.EMPTY;

    private refreshStatePeriods() {
        this.subscription.unsubscribe();
        if (this.explicitStatePeriods) {
            this.setStatePeriods(this.explicitStatePeriods);
            return;
        }
        this.statePeriods = [];
        this.activeStatePeriod = undefined;
        if (this.currentStudyRightId) {
            this.subscription = this.studyRightTermRegistrationsEntityService
                .getById(this.currentStudyRightId)
                .pipe(this.appErrorHandler.defaultErrorHandler())
                .subscribe({
                    next: srtr => this.setStatePeriods(srtr.statePeriods),
                });
        }
    }

    private setStatePeriods(statePeriods: StudyRightStatePeriod[]) {
        this.statePeriods = this.sortPeriods(statePeriods);
        this.activeStatePeriod = (this.statePeriods || []).find(({ startDate, endDate }) =>
            dateUtils.dateRangeContains(moment(), startDate, endDate),
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.studyRightIdSubscription) {
            this.studyRightIdSubscription.unsubscribe();
        }
    }

    getInclusiveEndDate(dateRange: { endDate: moment.MomentInput }) {
        return _.has(dateRange, 'endDate') ? moment(dateRange.endDate).subtract(1, 'days').format('YYYY-MM-DD') : null;
    }

    get currentPassivation(): StudyRightPassivation | null | undefined {
        if (this.activeStatePeriod?.state !== 'PASSIVATED') {
            return undefined;
        }
        return (this.studyRightPassivations ?? []).find(passivation =>
            dateUtils.dateRangeContains(moment(), passivation.valid?.startDate, passivation.valid?.endDate),
        );
    }

    getDecisionType(): StudyRightPassivation | StudyRightCancellation {
        if (this.isPassivated()) {
            return this.currentPassivation;
        }
        return this.studyRightCancellation;
    }

    isPassivated(): boolean {
        return !!(this.activeStatePeriod?.state === 'PASSIVATED' && this.currentPassivation);
    }

    isCancelled(): boolean {
        return !!(
            this.activeStatePeriod &&
            StudyRightStatesComponent.CANCELLED_STATES.includes(this.activeStatePeriod.state) &&
            this.studyRightCancellation
        );
    }

    showDecisionDetails() {
        this.modalService
            .open(StudyRightDecisionModalComponent, this.getDecisionType(), {
                size: 'sm',
            })
            .result.catch(() => {
                // The modal was dismissed => do nothing
            });
    }

    private sortPeriods(periods: StudyRightStatePeriod[]): StudyRightStatePeriod[] {
        return _.orderBy([...periods], [x => (x.startDate ? 1 : 0), 'startDate'], ['desc', 'desc']);
    }
}
